<template>
  <div class="form-container">
    <div class="mb-2" v-if="customerMonthlyBillingData?.id">
      <button
        class="btn btn-sm"
        :class="lang === 'ja' ? 'btn-primary' : 'btn-outline-primary'"
        @click="lang = 'ja'"
      >
        日本語
      </button>
      <button
        class="btn btn-sm mx-2"
        :class="lang === 'en' ? 'btn-primary' : 'btn-outline-primary'"
        @click="lang = 'en'"
      >
        English
      </button>
      
      <button
        class="btn btn-sm"
        :class="deliveryNote ? 'btn-primary' : 'btn-outline-primary'"
        @click="deliveryNote = !deliveryNote"
      >
        <span></span>納品書 {{ deliveryNote ? 'あり' : 'なし' }}
      </button>
      
      <button
        class="btn btn-sm mx-2"
        :class="sealFlg ? 'btn-primary' : 'btn-outline-primary'"
        @click="sealFlg = !sealFlg"
      >
        <span></span>押印 {{ sealFlg ? 'あり' : 'なし' }}
      </button>
    </div>

    <BillingEditJa
      v-if="lang === 'ja' && customerMonthlyBillingData?.id"
      :customer-monthly-billing-data="customerMonthlyBillingData"
      :buildings="buildings"
      :customers="customers"
      :grouped-billings="groupedBillings"
      :deliver-note="deliveryNote"
      :seal-flg="sealFlg"
      :is-submitting="isSubmitting"
      @add-row="addBillingRow"
      @remove-row="removeBillingRow"
      @update-totals="updateTotals"
      @go-back="goBack"
      @submit="requestUpdateMonthlyBilling"
    />

    <BillingEditEn
      v-if="lang === 'en' && customerMonthlyBillingData?.id"
      :customer-monthly-billing-data="customerMonthlyBillingData"
      :buildings="buildings"
      :customers="customers"
      :grouped-billings="groupedBillings"
      :deliver-note="deliveryNote"
      :seal-flg="sealFlg"
      :is-submitting="isSubmitting"
      @add-row="addBillingRow"
      @remove-row="removeBillingRow"
      @update-totals="updateTotals"
      @go-back="goBack"
      @submit="requestUpdateMonthlyBilling"
    />

    <AnnualWorkCancellationConfirmModal
      :show="showCancellationConfirm"
      :cancellations="annualWorkCancellations"
      :is-submitting="isSubmitting"
      mode="update"
      @close="showCancellationConfirm = false"
      @confirm="updateMonthlyBilling"
    />
  </div>
</template>

<script>
import { calculateBillingAmounts } from '@/utils/billingAmounts';
import { ref, computed, onMounted, watch, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import {
  getSelectBoxBuildings,
  getSelectBoxCustomers,
  getMonthlyBilling,
  updateMonthlyBillings
} from '@/api/axios';
import { sortAndNormalizeMonthlyBillingDetails } from '@/utils/monthlyBillingDetails';
import BillingEditJa from './BillingEditJa.vue';
import BillingEditEn from './BillingEditEn.vue';
import AnnualWorkCancellationConfirmModal from '@/components/invoices/AnnualWorkCancellationConfirmModal.vue';

export default {
  components: {
    BillingEditJa,
    BillingEditEn,
    AnnualWorkCancellationConfirmModal
  },
  setup() {
    const title = ref('請求書編集');

    watchEffect(() => {
      document.title = title.value;
    });

    const userId = ref('');
    const userName = ref('');
    const lang = ref('ja');
    const deliveryNote = ref(false);
    const sealFlg = ref(true);
    const customer = ref({});
    const buildings = ref([]);
    const customers = ref([]);
    const showCancellationConfirm = ref(false);
    const isSubmitting = ref(false);
    const errorMessages = ref({});

    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const billingId = route.params.id
    const year = route.params.year;
    const month = route.params.month;
    const today = new Date().getDate();

    const customerMonthlyBillingData = ref({
      m_customer_id: '',
      m_customer_representative_id: '',
      dear_nm: '',
      parent_billing_no: '',
      billing_year: '',
      billing_month: '',
      billing_dt: '',
      billing_dt_display_flg: '',
      representative_nm_display_flg: '',
      total_amount: '',
      billing_total_amount: '',
      tax_rate: '',
      bank_display_flg_01: '',
      bank_nm_01: '三菱UFJ銀行',
      branch_nm_01: '新宿中央支店',
      account_type_01: '当座',
      account_no_01: '3625559',
      bank_display_flg_02: '',
      bank_nm_02: 'りそな銀行',
      branch_nm_02: '新宿支店',
      account_type_02: '当座',
      account_no_02: '0511273',
      bank_display_flg_03: '',
      bank_nm_03: '三井住友銀行',
      branch_nm_03: '新宿西口支店',
      account_type_03: '普通',
      account_no_03: '9321051',
      carryover_amount: '',
      status: '',
      billing_count: '',
      billing_lang_type: '', // 0: 日本語, 1: 英語
      delivery_note_flg: '', // false: 納品書なし、true: 納品書あり
      billing_customer_address_display_flg: '', // 顧客住所表示フラグ
      honorific_title: '御中', // 敬称
      seal_flg: true, // 押印フラグ
      t_customer_monthly_billing_details: []
    });

    const fetchWorkScheduleDatas = async () => {
      userId.value = localStorage.getItem('userId');
      userName.value = localStorage.getItem('userName');

      try {
        const [
          buildingRes,
          customerRes,
          billingDataRes
        ] = await Promise.all([
          getSelectBoxBuildings(),
          getSelectBoxCustomers(),
          getMonthlyBilling(billingId, year, month)
        ]);

        // --- 建物情報 ---
        buildings.value = buildingRes.data;

        // --- 請求先顧客 ---
        customers.value = customerRes.data;

        // --- 請求データ ---
        const response = billingDataRes;
        customerMonthlyBillingData.value = response.data.monthly_billing;
        customerMonthlyBillingData.value.t_customer_monthly_billing_details = sortAndNormalizeMonthlyBillingDetails(
          (customerMonthlyBillingData.value.t_customer_monthly_billing_details || []).map((detail, index) => ({
            ...detail,
            client_row_key: `billing-detail-${detail.id || index}`,
            fixed_fee_summary: Boolean(detail.fixed_fee_summary),
            fixed_fee_billing: Boolean(detail.m_building?.fixed_fee_billing),
            annual_work_cancellation_allowed: (
              detail.work_type === 0 &&
              Boolean(detail.target_work_id) &&
              !detail.fixed_fee_summary
            ),
            cancel_annual_work: false
          }))
        );
        lang.value = customerMonthlyBillingData.value.billing_lang_type == 1 ? 'en' : 'ja';
        deliveryNote.value = customerMonthlyBillingData.value.delivery_note_flg;
        sealFlg.value = customerMonthlyBillingData.value.seal_flg;

        // 顧客情報を取得して、請求先と住所を設定
        customer.value = customerMonthlyBillingData.value?.m_customer || {};
        customerMonthlyBillingData.value.customer_address = format_address(customer.value) || '';

        // work_dt を監視し、年と月を設定
        customerMonthlyBillingData.value.t_customer_monthly_billing_details.forEach((billing) => {
          watchDateField(() => billing.work_dt, billing, 'work');
        });
        
        // billing_dt を監視し、年と月を設定
        watchDateField(
          () => customerMonthlyBillingData.value.billing_dt,
          customerMonthlyBillingData.value,
          'billing'
        );
      } catch (error) {
        console.error('請求情報の取得に失敗しました:', error);
      }
    };

    const applyBillingDetailOrdering = () => {
      customerMonthlyBillingData.value.t_customer_monthly_billing_details = sortAndNormalizeMonthlyBillingDetails(
        customerMonthlyBillingData.value.t_customer_monthly_billing_details || []
      );
    };

    // 言語変更を監視して billing_lang_type を更新
    watch(lang, (newLang) => {
      customerMonthlyBillingData.value.billing_lang_type = newLang === 'en' ? 1 : 0;
    });

    // 変更を監視して delivery_note を更新
    watch(deliveryNote, (newVal) => {
      customerMonthlyBillingData.value.delivery_note_flg = newVal ? true : false;
    });

    // 変更を監視して seal_flg を更新
    watch(sealFlg, (newVal) => {
      customerMonthlyBillingData.value.seal_flg = newVal ? true : false;
    });

    // グループ情報を計算する：どの建物IDが何行あるか、どのindexで出てくるかをまとめる
    const groupedBillings = computed(() => {
      const grouped = {};

      // 請求詳細を1件ずつ処理
      customerMonthlyBillingData.value.t_customer_monthly_billing_details.forEach((billing, index) => {
        // work_type==2（追加行）は無視
        if (billing.work_type === 2) return;

        const id = billing.m_building_id;
        
        // 初めて見た建物IDなら初期化
        if (!grouped[id]) {
          grouped[id] = {
            count: 0,       // 同じ建物IDの出現数
            indexes: []     // 各出現位置（index）
          };
        }

        // 数を1つ増やし、indexを記録
        grouped[id].count += 1;
        grouped[id].indexes.push(index);
      });

      return grouped;
    });

    // 請求項目行追加
    const addBillingRow = () => {
      customerMonthlyBillingData.value.t_customer_monthly_billing_details.push({
        t_billing_detail_monthly_billing_id: '',
        t_building_work_schedule_detail_id: '',
        m_building_id: '',
        work_type: 2,
        target_work_id: '',
        branch_billing_no: '',
        work_year: '',
        work_month: '',
        work_dt: '',
        work_content: '',
        unit_price: '',
        bill_amount: '',
        sort_no: customerMonthlyBillingData.value.t_customer_monthly_billing_details.length + 1
      });
      applyBillingDetailOrdering();
    };

    // 手入力の請求項目行削除
    const removeBillingRow = (index) => {
      const detail = customerMonthlyBillingData.value.t_customer_monthly_billing_details[index];
      if (detail?.work_type !== 2) return;

      customerMonthlyBillingData.value.t_customer_monthly_billing_details.splice(index, 1);
      applyBillingDetailOrdering();
    };

    const annualWorkCancellations = computed(() => (
      customerMonthlyBillingData.value.t_customer_monthly_billing_details || []
    ).filter((detail) => (
      detail.work_type === 0 &&
      detail.annual_work_cancellation_allowed &&
      detail.cancel_annual_work
    )).map((detail) => ({
      client_row_key: detail.client_row_key,
      t_building_work_schedule_detail_id: detail.target_work_id,
      target_work_year: Number(detail.work_year) || Number(year),
      target_work_month: Number(detail.work_month) || Number(month),
      building_nm: detail.m_building?.building_nm || '',
      work_content: detail.work_content || '',
      bill_amount: Number(detail.bill_amount) || 0,
      fixed_fee_billing: Boolean(detail.fixed_fee_billing),
      reason: '請求書更新時に作業未実施を確認'
    })));

    // 顧客情報から住所をフォーマットする関数（英語）
    const format_address = (customer) => {
      if (!customer) return '';
      const line1 = [customer.other_address, customer.city].filter(Boolean).join(', ');
      const line2 = [customer.prefecture, customer.postal_code].filter(Boolean).join(', ');
      return `${line1}\n${line2}`;
    };

    // 日付を監視して、変更があったら年と月を自動で詰める（空なら空にする）
    function watchDateField(source, target, prefix = '') {
      watch(
        () => typeof source === 'function' ? source() : source,
        (newVal) => {
          if (!newVal) {
            // 年間予定作業の対象年月は、実作業日が未入力でも保持する
            if (prefix === 'work' && Number(target.work_type) === 0) return;

            // ✅ newVal が null/''/undefined の場合は year, month を空に
            target[`${prefix}_year`] = '';
            target[`${prefix}_month`] = '';
            return;
          }

          const date = new Date(newVal);
          if (!isNaN(date.getTime())) {
            target[`${prefix}_year`] = date.getFullYear();
            target[`${prefix}_month`] = date.getMonth() + 1;
          } else {
            // ✅ 万が一フォーマットがおかしい場合も空にする保険
            target[`${prefix}_year`] = '';
            target[`${prefix}_month`] = '';
          }
        },
        { immediate: true }
      );
    }

    // 金額変更を監視して金額を更新
    watch(
      () => [
        customerMonthlyBillingData.value.t_customer_monthly_billing_details,
        customerMonthlyBillingData.value.carryover_amount,
        customerMonthlyBillingData.value.tax_rate
      ],
      ([newDetails]) => {
        // t_customer_monthly_billing_details の金額合計を計算
        const billing_detail_total = newDetails.reduce((sum, detail) => {
          if (detail.cancel_annual_work) return sum;
          return sum + (parseFloat(detail.bill_amount) || 0);
        }, 0);

        // 合計金額を更新
        customerMonthlyBillingData.value.total_amount = billing_detail_total;

        // 最終合計金額を更新
        updateTotals();
      },
      { deep: true } // 配列の内部要素の変更も追跡
    );

    // Format currency
    const formatCurrency = (value) => {
      if (value) {
        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency: 'JPY',
        }).format(value);
      }
      return '¥0';
    };

    const calculateTax = () => calculateBillingAmounts(customerMonthlyBillingData.value).tax_amount;

    // 合計金額の計算（繰越金額を除く）
    const calculateNoCarryoverTotal = () => calculateBillingAmounts(customerMonthlyBillingData.value).current_billing_amount;

    // 合計金額の計算（繰越金額を含む）
    const calculateGrandTotal = () => {
      const grandTotal = calculateBillingAmounts(customerMonthlyBillingData.value).billing_total_amount;
      customerMonthlyBillingData.value.billing_total_amount = grandTotal;
      return grandTotal;
    };

    const updateTotals = () => {
      calculateGrandTotal();
    };

    // 戻るボタンの挙動制御
    const goBack = () => {
      const { year, month, from } = route.query;

      if (from === 'list') {
        router.push({
          path: '/monthly-billings',
          query: { ...route.query, year, month }
        });
      } else if (from === 'detail') {
        router.push({
          path: `/monthly-billing-detail/${billingId}/${year}/${month}`,
          query: { ...route.query, year, month }
        });
      } else {
        // フォールバック（直接URLで編集画面開いたときなど）
        router.push({
          path: '/monthly-billings',
          query: { ...route.query, year, month }
        });
      }
    };

    const requestUpdateMonthlyBilling = () => {
      if (isSubmitting.value) return;
      if (annualWorkCancellations.value.length > 0) {
        showCancellationConfirm.value = true;
        return;
      }

      updateMonthlyBilling();
    };

    const updateMonthlyBilling = async () => {
      if (isSubmitting.value) return;

      isSubmitting.value = true;
      try {
        applyBillingDetailOrdering();

        const requestData = {
          ...customerMonthlyBillingData.value,
          annual_work_cancellations: annualWorkCancellations.value,
          t_customer_monthly_billing_details: customerMonthlyBillingData.value.t_customer_monthly_billing_details
            .filter((detail) => !detail.cancel_annual_work)
        };

        // APIにデータを送信
        // customerMonthlyBillingData.value.m_customer_id = customerRepresentatives.value.find(customerRepresentative => 
        //   customerRepresentative.id == customerMonthlyBillingData.value.m_customer_representative_id
        // )?.m_customer_id || null;

        await updateMonthlyBillings(billingId, requestData);
        store.dispatch('setFlashMessage', { message: '請求情報更新完了！', type: 'success' });
        router.push({
          path: `/monthly-billing-detail/${billingId}/${year}/${month}`,
          query: { ...route.query, year, month }
        });
      } catch (error) {
        console.error('更新中にエラーが発生しました:', error);
        const message = error.response?.data?.error || '請求書の更新に失敗しました。入力内容を確認してください。';
        store.dispatch('setFlashMessage', { message, type: 'danger' });
      } finally {
        isSubmitting.value = false;
        showCancellationConfirm.value = false;
      }
    };

    onMounted(() => {
      fetchWorkScheduleDatas();
    });

    return {
      title,
      year,
      month,
      lang,
      deliveryNote,
      sealFlg,
      today,
      buildings,
      customers,
      annualWorkCancellations,
      showCancellationConfirm,
      isSubmitting,
      groupedBillings,
      addBillingRow,
      removeBillingRow,
      formatCurrency,
      calculateTax,
      calculateNoCarryoverTotal,
      calculateGrandTotal,
      updateTotals,
      errorMessages,
      customerMonthlyBillingData,
      goBack,
      requestUpdateMonthlyBilling,
      updateMonthlyBilling
    };
  }
};
</script>

<style>
.form-container {
  width: 90% !important;
}
</style>
